import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { LockedExplainSheet } from "@/components/skills/LockedExplainSheet";
import { CapstoneStar, ScenStatusDot } from "@/components/skills/atoms";
import { useT } from "@/lib/i18n";
import { useScenarioTree, useScenarios, type ScenarioSummary, type ScenarioTreeItem } from "@/lib/scenarios";
import { cn } from "@/lib/utils";

/**
 * Map / Tree screen — vertical timeline of stages with their
 * scenarios as cells. Each cell color-codes via `ScenStatusDot`.
 * Capstones get a star + slightly elevated card.
 *
 * Mockup: `design/hifi/scenarios.jsx` `MapScreen` ~screen 7.
 *
 * Data: single round-trip to `/scenarios/tree`.
 */
export function SkillsTreePage() {
  const t = useT();
  const tree = useScenarioTree();
  // Side-fetch the journal-shaped list so the LockedExplainSheet can
  // resolve prereq slugs → titles + per-user statuses. The cache is
  // shared with the journal, so this is usually a no-op fetch.
  const scenarios = useScenarios();
  const [lockedFocus, setLockedFocus] = useState<ScenarioSummary | null>(null);

  // Promote a tree-item click into a LockedExplain sheet if the item
  // is hard-locked. We need the full ScenarioSummary (the sheet types
  // against it for `prerequisiteSlugs` + `progress.status` resolution
  // through `useScenarios`); look it up by slug from the cached list.
  const onLockedTap = (slug: string) => {
    const summary = (scenarios.data ?? []).find((s) => s.slug === slug);
    if (summary) setLockedFocus(summary);
  };

  if (tree.isLoading) {
    return <div className="flex min-h-[40vh] items-center justify-center text-sm text-text-3">…</div>;
  }
  if (tree.isError || !tree.data) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 text-center">
        <p className="text-sm text-[color:var(--again)]">
          {tree.error instanceof Error ? tree.error.message : t("common.failed_to_load")}
        </p>
        <Link to="/skills" className="text-[13px] text-text-3 hover:text-text">
          {t("skills.tree_back")}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 pb-8">
      {/* Sticky header — back arrow + title stay visible while the
          stage list scrolls. Negative `-mt-5` cancels the parent
          <main>'s `pt-5` and lets `pt-5` here fill that gap, so the
          background covers the whole top edge when scrolled and the
          header has the same resting position as before. The thin
          bottom border surfaces only after scroll lifts content
          under it (added unconditionally — at rest it just reads as
          a section divider). */}
      <div className="sticky top-0 z-20 -mx-4 -mt-5 border-b border-border/60 bg-bg/95 px-4 pt-5 pb-3 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <Link
            to="/skills"
            aria-label="back"
            className="-ml-1 rounded-md p-2 text-text-3 hover:bg-[color:var(--surface-2)]"
          >
            <ArrowLeft className="size-5" />
          </Link>
          <h1 className="text-[22px] font-bold tracking-tight">{t("skills.tree_title")}</h1>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {tree.data.stages.map((stage) => (
          <StageRow key={stage.stage} stage={stage.stage} items={stage.items} onLockedTap={onLockedTap} />
        ))}
      </div>

      <Legend />

      <LockedExplainSheet
        scenario={lockedFocus}
        onClose={() => setLockedFocus(null)}
        // Chain inside the sheet: a LOCKED prereq swaps the focus
        // instead of navigating away (no actionable Detail anyway
        // until its own prereqs clear).
        onFocus={(s) => setLockedFocus(s)}
      />
    </div>
  );
}

function StageRow({
  stage,
  items,
  onLockedTap,
}: {
  stage: number;
  items: ScenarioTreeItem[];
  onLockedTap: (slug: string) => void;
}) {
  const t = useT();
  // Stage progress = % completed. Drives the dot color in the gutter.
  const completed = items.filter((i) => i.status === "COMPLETED" || i.status === "NEEDS_REVIEW").length;
  const pct = items.length === 0 ? 0 : completed / items.length;

  return (
    <div className="flex gap-3">
      {/* Gutter dot + connector */}
      <div className="flex flex-col items-center pt-1">
        <div
          className="flex size-6 shrink-0 items-center justify-center rounded-full font-mono text-[10px] font-bold"
          style={{
            background: pct >= 1 ? "var(--scen-solid)" : pct > 0 ? "var(--scen-bg2)" : "var(--surface-2)",
            color: pct >= 1 ? "#fff" : "var(--scen-fg)",
          }}
        >
          {stage}
        </div>
        <div className="mt-1 w-px flex-1" style={{ background: "var(--border)" }} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider text-text-3">
          {t("skills.tree_stage", { n: String(stage) })}
        </div>
        <div className="flex flex-col gap-1.5">
          {items.map((item) => (
            <TreeCell key={item.slug} item={item} onLockedTap={onLockedTap} />
          ))}
        </div>
      </div>
    </div>
  );
}

function TreeCell({ item, onLockedTap }: { item: ScenarioTreeItem; onLockedTap: (slug: string) => void }) {
  const t = useT();
  const isHardLocked = item.status === "LOCKED";
  // Same row chrome for both navigation (link) and locked-explain
  // (button) variants — extracted so the two paths can't drift on
  // styling.
  const inner = (
    <>
      <ScenStatusDot status={item.status} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[13px] font-semibold text-text">{item.title}</span>
          <span
            className="shrink-0 rounded px-[5px] py-[1px] font-mono text-[9px] font-semibold text-text-3"
            style={{ background: "var(--surface-2)" }}
          >
            {item.level}
          </span>
          {item.capstone && (
            <span
              className="inline-flex shrink-0 items-center gap-[3px] rounded px-[5px] py-[1px] font-mono text-[9px] font-bold uppercase tracking-wider"
              style={{ background: "var(--scen-bg)", color: "var(--scen-fg)" }}
            >
              <CapstoneStar size={9} />
              {t("skills.tag_capstone")}
            </span>
          )}
        </div>
      </div>
    </>
  );

  const className = cn(
    "flex w-full items-center gap-3 rounded-[var(--r-md)] border bg-surface px-3 py-2.5 text-left transition-colors",
    isHardLocked && "opacity-[0.62]",
  );
  const style = { borderColor: item.capstone ? "var(--scen-bg2)" : "var(--border)" };

  // Hard-locked rows open the LockedExplain bottom sheet instead of
  // navigating — there's nothing actionable on the Detail page until
  // prereqs clear, and the sheet lists exactly those.
  if (isHardLocked) {
    return (
      <button type="button" onClick={() => onLockedTap(item.slug)} className={className} style={style}>
        {inner}
      </button>
    );
  }
  return (
    <Link to={`/skills/${item.slug}`} className={className} style={style}>
      {inner}
    </Link>
  );
}

function Legend() {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-[var(--r-md)] bg-[color:var(--surface-2)] px-3 py-2 font-mono text-[10px] text-text-3">
      <LegendItem status="COMPLETED" label={t("skills.tree_legend_completed")} />
      <LegendItem status="IN_PROGRESS" label={t("skills.tree_legend_in_progress")} />
      <LegendItem status="AVAILABLE" label={t("skills.tree_legend_available")} />
      <LegendItem status="LOCKED" label={t("skills.tree_legend_locked")} />
    </div>
  );
}

function LegendItem({ status, label }: { status: ScenarioTreeItem["status"]; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <ScenStatusDot status={status} />
      <span>{label}</span>
    </div>
  );
}
